import type { CampaignData, Ad, SiteLink, CalloutType, StructuredSnippetType, PriceType, PromotionType } from './types';

function fmt(value: string | number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function section(title: string, content: string): string {
  return `\n## ${title}\n\n${content}`;
}

function subsection(title: string, content: string): string {
  return `\n### ${title}\n\n${content}`;
}

function renderAd(ad: Ad, index: number): string {
  const adTitle = ad.googleads_ad_group_name || ad.name || ad.slug;
  const funnelStages = ad.ad_funnel_stage?.map((s) => s.funnel).join(', ') || ad.funnel_stage || '—';

  const lines: string[] = [
    `### Ad ${index + 1}: ${adTitle}`,
    '',
    `**State:** ${fmt(ad.state)} | **Funnel Stage:** ${funnelStages}${ad.is_paused ? ' | **PAUSED**' : ''}`,
  ];

  if (ad.keywords?.length) {
    lines.push('', '#### Keywords', '');
    lines.push('| Text | Match Type | Monthly Searches | Competition |');
    lines.push('|------|-----------|-----------------|-------------|');
    for (const kw of ad.keywords) {
      if (kw.is_deleted) continue;
      lines.push(
        `| ${fmt(kw.text)} | ${fmt(kw.match_type)} | ${fmt(kw.avg_monthly_searches)} | ${fmt(kw.competition)} |`,
      );
    }
  }

  if (ad.negative_keywords?.length) {
    lines.push('', '#### Negative Keywords', '');
    for (const kw of ad.negative_keywords) {
      lines.push(`- ${fmt(kw.text)} (${fmt(kw.match_type)})`);
    }
  }

  if (ad.alternative_keywords?.length) {
    lines.push('', '#### Alternative Keywords', '');
    for (const kw of ad.alternative_keywords) {
      lines.push(`- ${fmt(kw.text)} (${fmt(kw.match_type)})`);
    }
  }

  if (ad.headlines?.length) {
    lines.push('', '#### Headlines', '');
    lines.push('| # | Text | Pin | Type |');
    lines.push('|---|------|-----|------|');
    ad.headlines.forEach((h, i) => {
      const pin = h.pin_position != null ? `Position ${h.pin_position}` : '—';
      lines.push(`| ${i + 1} | ${fmt(h.text)} | ${pin} | ${fmt(h.type)} |`);
    });
  }

  if (ad.descriptions?.length) {
    lines.push('', '#### Descriptions', '');
    lines.push('| # | Text |');
    lines.push('|---|------|');
    ad.descriptions.forEach((d, i) => {
      lines.push(`| ${i + 1} | ${fmt(d.text)} |`);
    });
  }

  return lines.join('\n');
}

function renderSitelinks(sitelinks: SiteLink[]): string {
  return sitelinks
    .map(
      (sl) =>
        `**${fmt(sl.name)}**\n- URL: ${fmt(sl.url)}\n- ${fmt(sl.description1)}\n- ${fmt(sl.description2)}`,
    )
    .join('\n\n');
}

function renderCallouts(callouts: CalloutType[]): string {
  return callouts.map((c) => `- ${fmt(c.text)}`).join('\n');
}

function renderStructuredSnippets(snippets: StructuredSnippetType[]): string {
  return snippets
    .map((s) => `**${fmt(s.header_type)}**\n${s.values.map((v) => `- ${v.value}`).join('\n')}`)
    .join('\n\n');
}

function renderPrices(prices: PriceType[]): string {
  return prices
    .map(
      (p) =>
        `**${fmt(p.header)}**\n- Type: ${fmt(p.type)} | Qualifier: ${fmt(p.qualifier)}\n- ${fmt(p.value)} ${fmt(p.currency)} / ${fmt(p.frequency)}\n- ${fmt(p.description)}\n- URL: ${fmt(p.final_url)}`,
    )
    .join('\n\n');
}

function renderPromotions(promotions: PromotionType[]): string {
  return promotions
    .map((p) => {
      const discount =
        p.discount_type === 'percent off'
          ? `${p.discount_modifier}% off`
          : `${fmt(p.currency)} ${p.discount_modifier} off`;
      const dates =
        p.start_date && p.end_date ? `${p.start_date} – ${p.end_date}` : p.start_date || p.end_date || '—';
      return [
        `**${fmt(p.promotion_target)}**`,
        `- Discount: ${discount}`,
        p.occasion ? `- Occasion: ${p.occasion}` : null,
        p.promotion_code ? `- Code: ${p.promotion_code}` : null,
        `- Dates: ${dates}`,
      ]
        .filter(Boolean)
        .join('\n');
    })
    .join('\n\n');
}

export function generateCampaignMarkdown(campaign: CampaignData): string {
  const parts: string[] = [];

  parts.push(`# Campaign: ${campaign.name}`);

  // Overview
  const overviewLines = [
    `- **State:** ${fmt(campaign.state)}`,
    `- **Goal:** ${fmt(campaign.goal)}`,
    `- **Website:** ${fmt(campaign.website_url)}`,
    campaign.business_description ? `- **Description:** ${campaign.business_description}` : null,
    `- **Created:** ${fmt(campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : undefined)}`,
    campaign.first_published_at
      ? `- **First Published:** ${new Date(campaign.first_published_at).toLocaleDateString()}`
      : null,
    `- **Campaign ID:** ${campaign.id}`,
    campaign.origin ? `- **Origin:** ${campaign.origin}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  parts.push(section('Overview', overviewLines));

  // Account & Google Ads
  const accountLines = [
    campaign.googleads_account?.name ? `- **Account Name:** ${campaign.googleads_account.name}` : null,
    campaign.googleads_account?.googleads_account_id
      ? `- **Account ID:** ${campaign.googleads_account.googleads_account_id}`
      : null,
    campaign.googleads_account?.currency ? `- **Currency:** ${campaign.googleads_account.currency}` : null,
    campaign.googleads_campaign?.googleads_campaign_id
      ? `- **Google Ads Campaign ID:** ${campaign.googleads_campaign.googleads_campaign_id}`
      : null,
    campaign.googleads_campaign?.advertising_channel_type
      ? `- **Channel Type:** ${campaign.googleads_campaign.advertising_channel_type}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');
  if (accountLines) parts.push(section('Account & Google Ads', accountLines));

  // Budget & Bidding
  const currency = campaign.googleads_account?.currency || 'USD';
  const biddingLines = [
    `- **Daily Budget:** ${currency} ${fmt(campaign.budget)}`,
    `- **Bidding Strategy:** ${fmt(campaign.bidding_strategy_type)}`,
    campaign.target_cpa ? `- **Target CPA:** ${currency} ${campaign.target_cpa}` : null,
    campaign.target_roas ? `- **Target ROAS:** ${campaign.target_roas}%` : null,
    campaign.target_cpa_explanation ? `- **CPA Explanation:** ${campaign.target_cpa_explanation}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  parts.push(section('Budget & Bidding', biddingLines));

  // Targeting
  const targetingParts: string[] = [];
  if (campaign.target_locations?.length) {
    const locationList = campaign.target_locations.map((l) => `- ${l.canonical_name}`).join('\n');
    targetingParts.push(subsection('Locations', locationList));
  }
  const geoLines = [
    `- **Positive Targeting:** ${fmt(campaign.positive_geo_target_type)}`,
    `- **Negative Targeting:** ${fmt(campaign.negative_geo_target_type)}`,
  ].join('\n');
  targetingParts.push(subsection('Geo Targeting Mode', geoLines));
  parts.push(section('Targeting', targetingParts.join('')));

  // Assets
  const assetParts: string[] = [];
  if (campaign.site_links?.length) {
    assetParts.push(subsection(`Sitelinks (${campaign.site_links.length})`, renderSitelinks(campaign.site_links)));
  }
  if (campaign.callouts?.length) {
    assetParts.push(subsection(`Callouts (${campaign.callouts.length})`, renderCallouts(campaign.callouts)));
  }
  if (campaign.account_callouts?.length) {
    const inherited = campaign.account_callouts.map((c) => `- ${c.callout_text}`).join('\n');
    // Account callouts stop serving once the campaign has its own (#6353).
    const label = campaign.callouts?.length
      ? 'Account Callouts (overridden, not serving on this campaign)'
      : 'Account Callouts (inherited)';
    assetParts.push(subsection(label, inherited));
  }
  if (campaign.structured_snippets?.length) {
    assetParts.push(
      subsection(`Structured Snippets (${campaign.structured_snippets.length})`, renderStructuredSnippets(campaign.structured_snippets)),
    );
  }
  if (campaign.prices?.length) {
    assetParts.push(subsection(`Prices (${campaign.prices.length})`, renderPrices(campaign.prices)));
  }
  if (campaign.promotions?.length) {
    assetParts.push(subsection(`Promotions (${campaign.promotions.length})`, renderPromotions(campaign.promotions)));
  }
  if (campaign.phone_numbers?.length) {
    const phones = campaign.phone_numbers
      .map((p) => `- ${p.value}${p.country_code ? ` (${p.country_code})` : ''} — Call Extension: ${p.call_extension_enabled}`)
      .join('\n');
    assetParts.push(subsection(`Phone Numbers (${campaign.phone_numbers.length})`, phones));
  }
  if (assetParts.length) {
    parts.push(section('Assets', assetParts.join('')));
  }

  // Ads
  if (campaign.ads?.length) {
    const adContent = campaign.ads.map((ad, i) => renderAd(ad, i)).join('\n\n---\n\n');
    parts.push(section(`Ads (${campaign.ads.length})`, adContent));
  }

  return parts.join('\n') + '\n';
}

export function downloadCampaignMarkdown(campaign: CampaignData): void {
  const md = generateCampaignMarkdown(campaign);
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${campaign.slug}.md`;
  a.click();
  URL.revokeObjectURL(url);
}
